/**
 * NotificationStore - MobX store for managing notifications.
 *
 * Handles toast notifications (top-right) and blocking notifications (center modal).
 */

import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';
import type { ReactNode } from 'react';

/**
 * Toast notification type.
 */
export type ToastType = 'info' | 'success' | 'warning' | 'error';

/**
 * Toast notification configuration.
 */
export interface Toast {
  /** Unique identifier */
  id: string;
  /** Notification type (affects styling) */
  type: ToastType;
  /** Title text */
  title: string;
  /** Optional message body */
  message?: string;
  /** Duration in ms (null = manual dismiss only) */
  duration: number | null;
  /** Optional custom icon */
  icon?: ReactNode;
  /** Timestamp when the toast was created */
  createdAt: number;
}

/**
 * Input for creating a toast (id is auto-generated).
 */
export interface ToastInput {
  /** Notification type (affects styling) */
  type: ToastType;
  /** Title text */
  title: string;
  /** Optional message body */
  message?: string;
  /** Duration in ms (null = manual dismiss only). Defaults to 5000ms. */
  duration?: number | null;
  /** Optional custom icon */
  icon?: ReactNode;
}

/**
 * Blocking notification that requires user dismissal.
 */
export interface BlockingNotification {
  /** Unique identifier */
  id: string;
  /** Dialog title */
  title: string;
  /** Content to render in the dialog body */
  content: ReactNode;
  /** Optional callback when notification is dismissed */
  onClose?: () => void;
  /** Whether to show the close button */
  showCloseButton?: boolean;
}

/**
 * Input for creating a blocking notification.
 */
export type BlockingNotificationInput = Omit<BlockingNotification, 'id'>;

/** Maximum number of toasts to display at once */
const MAX_TOASTS = 5;

/** Default toast duration in ms */
const DEFAULT_TOAST_DURATION = 5000;

/**
 * NotificationStore manages toast and blocking notifications.
 *
 * @example
 * ```tsx
 * const { notificationStore } = useRootStore();
 *
 * // Show a toast
 * notificationStore.addToast({
 *   type: 'success',
 *   title: 'Level Up!',
 *   message: 'You reached Mining level 10',
 * });
 *
 * // Show a blocking notification
 * notificationStore.showBlocking({
 *   title: 'Welcome Back!',
 *   content: <OfflineProgressSummary />,
 * });
 * ```
 */
export class NotificationStore {
  /** Active toast notifications */
  toasts: Toast[] = [];

  /** Currently displayed blocking notification (null if none) */
  blockingNotification: BlockingNotification | null = null;

  /** Queue of blocking notifications waiting to be shown */
  private blockingQueue: BlockingNotification[] = [];

  /** Map of timeout IDs for auto-dismissing toasts */
  private toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(_rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // =========================================
  // Toast Methods
  // =========================================

  /**
   * Add a toast notification.
   *
   * @param input - Toast configuration
   * @returns The toast ID
   */
  addToast(input: ToastInput): string {
    const id = `toast-${String(Date.now())}-${Math.random().toString(36).slice(2, 9)}`;
    const duration = input.duration === undefined ? DEFAULT_TOAST_DURATION : input.duration;

    const toast: Toast = {
      ...input,
      id,
      duration,
      createdAt: Date.now(),
    };

    runInAction(() => {
      this.toasts.push(toast);

      // Remove oldest toasts if over limit
      while (this.toasts.length > MAX_TOASTS) {
        const oldest = this.toasts[0];
        if (oldest) {
          this.removeToast(oldest.id);
        }
      }
    });

    // Schedule auto-dismiss if duration is set
    if (duration !== null) {
      const timeoutId = setTimeout(() => {
        this.removeToast(id);
      }, duration);
      this.toastTimeouts.set(id, timeoutId);
    }

    return id;
  }

  /**
   * Remove a toast by ID.
   */
  removeToast(id: string): void {
    // Clear any pending timeout
    const timeoutId = this.toastTimeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.toastTimeouts.delete(id);
    }

    runInAction(() => {
      this.toasts = this.toasts.filter((toast) => toast.id !== id);
    });
  }

  /**
   * Clear all toasts.
   */
  clearToasts(): void {
    // Clear all timeouts
    for (const timeoutId of this.toastTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.toastTimeouts.clear();

    runInAction(() => {
      this.toasts = [];
    });
  }

  // =========================================
  // Convenience Toast Methods
  // =========================================

  /**
   * Show an info toast.
   */
  info(title: string, message?: string): string {
    const input: ToastInput = { type: 'info', title };
    if (message !== undefined) {
      input.message = message;
    }
    return this.addToast(input);
  }

  /**
   * Show a success toast.
   */
  success(title: string, message?: string): string {
    const input: ToastInput = { type: 'success', title };
    if (message !== undefined) {
      input.message = message;
    }
    return this.addToast(input);
  }

  /**
   * Show a warning toast.
   */
  warning(title: string, message?: string): string {
    const input: ToastInput = { type: 'warning', title };
    if (message !== undefined) {
      input.message = message;
    }
    return this.addToast(input);
  }

  /**
   * Show an error toast.
   */
  error(title: string, message?: string): string {
    const input: ToastInput = { type: 'error', title, duration: null }; // Errors require manual dismiss
    if (message !== undefined) {
      input.message = message;
    }
    return this.addToast(input);
  }

  // =========================================
  // Blocking Notification Methods
  // =========================================

  /**
   * Show a blocking notification.
   *
   * If a blocking notification is already visible, the new one is queued.
   */
  showBlocking(input: BlockingNotificationInput): string {
    const id = `blocking-${String(Date.now())}-${Math.random().toString(36).slice(2, 9)}`;
    const notification: BlockingNotification = {
      ...input,
      id,
      showCloseButton: input.showCloseButton ?? true,
    };

    runInAction(() => {
      if (this.blockingNotification === null) {
        this.blockingNotification = notification;
      } else {
        // Queue for later
        this.blockingQueue.push(notification);
      }
    });

    return id;
  }

  /**
   * Dismiss the current blocking notification.
   *
   * Shows the next queued notification if any.
   */
  dismissBlocking(): void {
    const current = this.blockingNotification;

    // Call the onClose callback if present
    if (current?.onClose) {
      current.onClose();
    }

    runInAction(() => {
      // Show next queued notification or clear
      if (this.blockingQueue.length > 0) {
        this.blockingNotification = this.blockingQueue.shift() ?? null;
      } else {
        this.blockingNotification = null;
      }
    });
  }

  /**
   * Check if there's a blocking notification active.
   */
  get hasBlockingNotification(): boolean {
    return this.blockingNotification !== null;
  }

  /**
   * Get the number of queued blocking notifications.
   */
  get blockingQueueLength(): number {
    return this.blockingQueue.length;
  }

  // =========================================
  // Reset
  // =========================================

  /**
   * Reset the notification store.
   */
  reset(): void {
    this.clearToasts();
    runInAction(() => {
      this.blockingNotification = null;
      this.blockingQueue = [];
    });
  }
}
